import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInQuarters,
  differenceInWeeks,
  differenceInYears,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  isAfter,
  isEqual,
  isValid,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays
} from 'date-fns'
import { Enums } from '@dolfin/business/db_types'
import { i18n } from '@lingui/core'
import { DateFormatter } from '@internationalized/date'

export const READABLE_DATE_FORMAT = 'MMM dd, yyyy'
export const ISO_DATE_FORMAT = 'yyyy-MM-dd' // ISO 8601

// Approximate duration thresholds in milliseconds
const MS_PER_DAY = 86400000
const YEAR_THRESHOLD_MS = 364 * MS_PER_DAY // ~364 days for year-length periods
const QUARTER_THRESHOLD_MS = 89 * MS_PER_DAY // ~89 days for quarter-length periods
const HALF_YEAR_THRESHOLD_MS = 181 * MS_PER_DAY // ~181 days for half-year periods

export const toUtcDateOnlyString = (date: Date): string => {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

type TimeZoneSource = Record<string, unknown> | null | undefined

const getTimeZoneCandidate = (source: TimeZoneSource): string | null => {
  if (!source || typeof source !== 'object') return null

  const timezone =
    (typeof source.timezone === 'string' && source.timezone) ||
    (typeof source.time_zone === 'string' && source.time_zone) ||
    ''

  const trimmed = timezone.trim()
  return trimmed.length > 0 ? trimmed : null
}

const formatDateOnlyInTimeZone = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const year = parts.find(part => part.type === 'year')?.value
  const month = parts.find(part => part.type === 'month')?.value
  const day = parts.find(part => part.type === 'day')?.value

  if (!year || !month || !day) {
    throw new Error(`Failed to format date in timezone "${timeZone}"`)
  }

  return `${year}-${month}-${day}`
}

export const toLocalDateOnlyString = (
  date: Date,
  ...sources: TimeZoneSource[]
): string => {
  const preferredTimeZone =
    sources.map(getTimeZoneCandidate).find(Boolean) ??
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    'UTC'

  try {
    return formatDateOnlyInTimeZone(date, preferredTimeZone)
  } catch {
    return formatDateOnlyInTimeZone(date, 'UTC')
  }
}

export const formatWithLocale = (
  date: string | Date,
  formatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'long' }
): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
    return ''
  }
  const formatter = new DateFormatter(i18n.locale || 'en-US', formatOptions)
  return formatter.format(parsedDate)
}

export const parseDateValue = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  // Preserve the previous app behavior for date-only strings used by period
  // labels. `new Date('YYYY-MM-DD')` matches the legacy callers here, while
  // `parseISO` treats those strings as local-midnight dates and can shift
  // duration-based labels across DST boundaries.
  const parsedDate = new Date(value)
  return isValid(parsedDate) ? parsedDate : null
}

export const startOfHalfYear = (date: Date): Date => {
  const quarter = getQuarter(date)
  if (quarter > 2) {
    return startOfQuarter(addQuarters(startOfYear(date), 2))
  }

  return startOfYear(date)
}

export const endOfHalfYear = (date: Date): Date => {
  const quarter = getQuarter(date)
  if (quarter < 3) {
    return endOfQuarter(addQuarters(startOfYear(date), 1))
  }

  return endOfYear(date)
}

const getHalfYearDisplayLabel = (date: Date): string => {
  const halfYearStart = startOfHalfYear(date)
  const halfYearEnd = endOfHalfYear(date)

  return `${formatWithLocale(halfYearStart, {
    month: 'long'
  })} - ${formatWithLocale(halfYearEnd, {
    month: 'long',
    year: 'numeric'
  })}`
}

export const getPeriodStartEnd = (
  period: Enums<'period'>,
  date: Date
): { start: string; end: string } => {
  switch (period) {
    case 'month':
      return {
        start: format(startOfMonth(date), ISO_DATE_FORMAT),
        end: format(endOfMonth(date), ISO_DATE_FORMAT)
      }
    case 'quarter':
      return {
        start: format(startOfQuarter(date), ISO_DATE_FORMAT),
        end: format(endOfQuarter(date), ISO_DATE_FORMAT)
      }
    case 'half_year':
      return {
        start: format(startOfHalfYear(date), ISO_DATE_FORMAT),
        end: format(endOfHalfYear(date), ISO_DATE_FORMAT)
      }
    case 'year':
      return {
        start: format(startOfYear(date), ISO_DATE_FORMAT),
        end: format(endOfYear(date), ISO_DATE_FORMAT)
      }
  }
}
export const getMonthDateRange = (referenceMonth: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(referenceMonth)
  if (!match) return null

  const [, yearString, monthString] = match
  const year = Number(yearString)
  const month = Number(monthString)
  if (!year || month < 1 || month > 12) return null

  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year
  const startMonth = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`

  return {
    start: `${startMonth}-01`,
    end: `${String(nextYear).padStart(4, '0')}-${String(nextMonth).padStart(2, '0')}-01`
  }
}

export const getPeriodFromDates = (
  start: string,
  end: string
): Enums<'period'> => {
  const startDate = parseISO(start)
  const endDate = parseISO(end)

  const normalizedStart = startOfDay(startDate)
  const normalizedEnd = startOfDay(endDate)

  if (isEqual(normalizedEnd, startOfDay(endOfMonth(normalizedStart)))) {
    return 'month'
  }

  if (isEqual(normalizedEnd, startOfDay(endOfQuarter(normalizedStart)))) {
    return 'quarter'
  }

  if (isEqual(normalizedEnd, startOfDay(endOfHalfYear(normalizedStart)))) {
    return 'half_year'
  }

  if (isEqual(normalizedEnd, startOfDay(endOfYear(normalizedStart)))) {
    return 'year'
  }

  return 'month'
}

export const isPeriodLessThan = (
  a: Enums<'period'>,
  b: Enums<'period'>
): boolean => {
  const order: Record<Enums<'period'>, number> = {
    month: 1,
    quarter: 2,
    half_year: 3,
    year: 4
  }

  return order[a] < order[b]
}

/**
 * Returns month name year name H{index} (for period of half year) or Q{index} (for period of quarter)
 * @param start
 * @param end
 */
export const getPeriodLabelFromStartAndEndDate = (
  start: string,
  end: string
) => {
  const startDate = parseDateValue(start)

  if (!startDate) {
    return ''
  }

  const endDate = parseDateValue(end)

  const year = startDate.getFullYear()
  const month = startDate.getMonth()

  const period = format(startDate, 'MMM')

  if (!endDate) {
    return `${period} ${year}`
  }

  if (endDate.getMonth() - month === 2) {
    return `Q${Math.floor(month / 3) + 1} ${year}`
  }

  if (endDate.getFullYear() > year) {
    return `${year} - ${endDate.getFullYear()}`
  }

  if (endDate.getMonth() - month === 11) {
    return `${year}`
  }

  if (endDate.getMonth() - month > 0) {
    return `${period} - ${format(endDate, 'MMM')} ${year}`
  }

  return `${period} ${year}`
}

export const getDisplayPeriodLabel = ({
  start,
  end,
  period
}: {
  start?: string | Date | null
  end?: string | Date | null
  period?: Enums<'period'>
}): string | null => {
  const startDate = parseDateValue(start)

  if (!startDate) {
    return null
  }

  if (period === 'year') {
    return format(startDate, 'yyyy')
  }

  if (period === 'quarter') {
    return format(startDate, 'qqq yyyy')
  }

  if (period === 'half_year') {
    return getHalfYearDisplayLabel(startDate)
  }

  if (period === 'month') {
    return formatWithLocale(startDate, {
      month: 'long',
      year: 'numeric'
    })
  }

  const endDate = parseDateValue(end)

  if (!endDate) {
    return formatWithLocale(startDate, {
      month: 'long',
      year: 'numeric'
    })
  }

  const duration = endDate.getTime() - startDate.getTime()

  if (duration >= YEAR_THRESHOLD_MS) {
    return format(endDate, 'yyyy')
  }

  if (duration >= HALF_YEAR_THRESHOLD_MS) {
    return getHalfYearDisplayLabel(endDate)
  }

  if (duration >= QUARTER_THRESHOLD_MS) {
    return format(endDate, 'qqq yyyy')
  }

  return formatWithLocale(endDate, {
    month: 'long',
    year: 'numeric'
  })
}

export const beautifyDateRange = (start: string, end: string) => {
  const startDate = new Date(start)
  const endDate = new Date(end)

  const startMonth = startDate.getMonth()
  const endMonth = endDate.getMonth()

  const startYear = startDate.getFullYear()
  const endYear = endDate.getFullYear()

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${format(startDate, 'dd')} - ${format(endDate, 'dd MMM')}`
    }

    return `${format(startDate, 'dd MMM')} - ${format(
      endDate,
      'dd MMM'
    )} ${endYear}`
  }

  return `${format(startDate, 'dd MMM yyyy')} - ${format(
    endDate,
    'dd MMM yyyy'
  )}`
}

export const isCurrentOrFutureMonth = (date: Date): boolean => {
  return (
    isSameMonth(date, new Date()) ||
    isAfter(startOfMonth(date), startOfMonth(new Date()))
  )
}

export const isCurrentMonth = (date: Date): boolean => {
  return isSameMonth(date, new Date())
}

export const parseDateRange = (dateRange: string) => {
  const [start, end] = dateRange
    .replaceAll('[', '')
    .replaceAll(')', '')
    .split(',')

  return {
    start,
    end: end
      ? format(subDays(parseISO(end.trim()), 1), ISO_DATE_FORMAT)
      : undefined
  }
}

export const isInRange = (
  date: string,
  range: { start: string; end?: string }
) => {
  const dateToCheck = parseISO(date)
  const startDate = parseISO(range.start)
  const endDate = range.end ? parseISO(range.end) : undefined
  return (
    (isAfter(dateToCheck, startDate) || isEqual(dateToCheck, startDate)) &&
    (!endDate || isAfter(endDate, dateToCheck) || isEqual(dateToCheck, endDate))
  )
}

export const getAbsoluteDifferenceInDays = (start: Date, end: Date) => {
  return Math.trunc(
    differenceInHours(
      new Date(end.setHours(23, 59, 59)),
      new Date(start.setHours(0, 0, 0))
    ) / 24
  )
}

export const isValidDate = (date: string) => {
  return !isNaN(parseISO(date).getTime())
}

export const isValidDateOnlyString = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) {
    return false
  }

  const [, yearStr, monthStr, dayStr] = match
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  const date = new Date(Date.UTC(year, month - 1, day))

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  )
}

export const differenceInPeriod = {
  day: differenceInDays,
  week: differenceInWeeks,
  month: differenceInMonths,
  quarter: differenceInQuarters,
  year: differenceInYears
}
export const addPeriods = {
  day: addDays,
  week: addWeeks,
  month: addMonths,
  quarter: addQuarters,
  year: addYears
}

export const sumPeriods = (
  startDate: string,
  numOfPeriods: number,
  period: 'day' | 'week' | 'month' | 'quarter' | 'year'
): string =>
  format(addPeriods[period](new Date(startDate), numOfPeriods), 'yyyy-MM-dd')
